import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchProducts } from '@/lib/productApi';
import { fetchAutoDiscount } from '@/lib/discountApi';
import type { Product, RoutineSet } from '@/types/product';
import type { Discount } from '@/types/discount';

interface ProductsContextType {
  products: Product[];
  routineSet: RoutineSet | null;
  // The 3-Step Set as a full product (new-design 3-Step page).
  setProduct: Product | null;
  loading: boolean;
  // The active sitewide/automatic discount (no code needed), if any — used
  // to show a "was/now" price on product pages. Coupon-code discounts are
  // intentionally never shown here; a customer has to enter those.
  autoDiscount: Discount | null;
}

const ProductsContext = createContext<ProductsContextType>({
  products: [],
  routineSet: null,
  setProduct: null,
  loading: true,
  autoDiscount: null,
});

export function ProductsProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [routineSet, setRoutineSet] = useState<RoutineSet | null>(null);
  const [setProduct, setSetProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoDiscount, setAutoDiscount] = useState<Discount | null>(null);

  useEffect(() => {
    fetchProducts()
      .then((result) => {
        setProducts(result.products);
        setRoutineSet(result.routineSet);
        setSetProduct(result.setProduct);
      })
      .finally(() => setLoading(false));
    fetchAutoDiscount().then(setAutoDiscount);
  }, []);

  return <ProductsContext.Provider value={{ products, routineSet, setProduct, loading, autoDiscount }}>{children}</ProductsContext.Provider>;
}

export function useProducts() {
  return useContext(ProductsContext);
}
